import React, { Suspense, useCallback, useRef, useState } from 'react';
import {
    extractFunctionBodyFromEditorText,
    wrapFunctionBodyForEditor,
} from '../../../services/worker-configuration/automation/worker-function-prompt';
import * as S from './configuration-rule.styles';

const LazyFunctionCodeEditor = React.lazy(() => import('./configuration-function-editor-lazy'));

export interface IConfigurationFunctionEditorProps {
    disabled?: boolean;
    onChange: (value: string) => void;
    value: string;
}

/**
 * The JS a rule's EXECUTE_ENTER_FUNCTION action runs against the claim. Always editable by hand,
 * whether or not `ConfigurationFunctionBuilder` (the AI "Build the function" flow, one level up in
 * `configuration-rule-card.tsx`) is available — this box is the manual fallback and the place any
 * AI-generated code lands for review, never a black box the AI writes to directly.
 *
 * The signature line and closing brace are shown as ordinary text inside the same editor as the
 * body — nothing here is locked — but they are cosmetic: `value`/`onChange` only ever carry the
 * body, and the argument the backend actually binds is always `payload` regardless of what is
 * typed on those two lines (see `worker-function-prompt.ts`).
 *
 * The visible text is local state, re-derived from `value` only when `value` changes for a reason
 * OTHER than this component's own last `onChange` call (the AI builder writes new code, or a
 * different rule's value is now bound here). Re-wrapping on every render from `value` directly —
 * the previous approach — meant any edit to the signature/closing-brace lines got silently
 * reverted on the very next render, since re-wrapping the (unaffected) body always reproduces the
 * canonical text: the editor looked editable there but was not (PR review, 2026-09-28).
 *
 * Syntax / forbidden-pattern errors are NOT shown here — they used to be, duplicating the field
 * error `configuration-rule-card.tsx` already renders below this component (same checks, same
 * `actionValue.function`, both live on every keystroke via the observer re-render). One message,
 * in the same place every other field in this form shows its error, not two.
 */
export const ConfigurationFunctionEditor: React.FC<IConfigurationFunctionEditorProps> = ({
    disabled,
    onChange,
    value,
}) => {
    // The body we ourselves last reported via `onChange` — used to tell an external `value` change
    // apart from the echo of our own last edit on the next render (see the class doc above).
    const lastEmittedBody = useRef(value);
    const [editorText, setEditorText] = useState(() => wrapFunctionBodyForEditor(value));

    if (value !== lastEmittedBody.current) {
        lastEmittedBody.current = value;
        setEditorText(wrapFunctionBodyForEditor(value));
    }

    const handleEditorChange = useCallback(
        (nextText: string) => {
            setEditorText(nextText);
            const body = extractFunctionBodyFromEditorText(nextText);
            lastEmittedBody.current = body;
            onChange(body);
        },
        [onChange],
    );

    return (
        <S.FunctionEditorShell>
            <S.FunctionEditorFrame>
                <Suspense fallback={<S.FunctionEditorLoading>Loading editor…</S.FunctionEditorLoading>}>
                    <LazyFunctionCodeEditor value={editorText} onChange={handleEditorChange} disabled={disabled} />
                </Suspense>
            </S.FunctionEditorFrame>
        </S.FunctionEditorShell>
    );
};
